import { intersection, keyBy } from 'es-toolkit';
import React, { useCallback, useMemo } from 'react';
import { TableTreeColumn } from '@devexpress/dx-react-grid-material-ui';

import { getBundlesByScheduleGroupGuid } from '../../selectors/getBundlesByScheduleGroupGuid';
import { GridRow } from '../../selectors/scheduleGroupGrid';
import { useAppSelector } from '../hooks';
import { CustomTreeCell } from './CustomTreeCell';
import { getDescendantRowsGetter } from './scheduleGroupGridUtilities';

interface ScheduleGroupGridTableTreeColumnCellProps {
  readonly rows: readonly GridRow[];
  readonly checkedRowIds: readonly string[];
  readonly rowSelectionDisabled: (row: GridRow) => boolean;
}

export function ScheduleGroupGridTableTreeColumnCell(
  props: ScheduleGroupGridTableTreeColumnCellProps & TableTreeColumn.CellProps,
) {
  const { rows, checkedRowIds, ...otherProps } = props;
  const bundlesByScheduleGroupGuid = useAppSelector(getBundlesByScheduleGroupGuid);

  const rowsKeyedByRowId = useMemo(() => keyBy(rows, (row) => row.rowId), [rows]);
  const getDescendantRows = useMemo(
    () => getDescendantRowsGetter(rowsKeyedByRowId, bundlesByScheduleGroupGuid),
    [rowsKeyedByRowId, bundlesByScheduleGroupGuid],
  );

  const rowSelectionIndeterminate = useCallback(
    (checkedRows: readonly string[]) =>
      function rowSelectionIndeterminateWithCheckedRows(row: GridRow): boolean {
        const { rowId, type } = row;
        if (checkedRows.includes(rowId) || type === 'groupedProductionComponentInstance') return false;

        const descendantRows = getDescendantRows(rowId);
        const descendantRowIds = descendantRows.map((row) => row.rowId);
        const numberOfCheckedDescendantRowIds = intersection(checkedRows, descendantRowIds).length;
        return numberOfCheckedDescendantRowIds > 0 && numberOfCheckedDescendantRowIds < descendantRowIds.length;
      },
    [getDescendantRows],
  );

  return <CustomTreeCell rowSelectionIndeterminate={rowSelectionIndeterminate(checkedRowIds)} {...otherProps} />;
}
